import styles from './webkua-chart.module.scss';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import PageTitle from '../page-title/page-title';
import { UseChartHook } from '../requestUtils';
import WebkuaToolBar from '../webkua-tool-bar/webkua-tool-bar';
import { Alert, Box, CheckboxLabel, Container, groupBy } from '@frontend-workspace/react-shared-lib';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { Bar, Line } from 'react-chartjs-2';
import { ChartData } from '../product-usage-chart/product-usage-chart';
import SalesChartSkeleton from '../sales-chart/sales-chart-skeleton';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface WebkuaChartProps {
  url: string;
  title: string;
  startDate?: Date;
  endDate?: Date;
}

export function WebkuaChart(props: WebkuaChartProps) {
  const { t } = useTranslation();
  const { data, onDateChange, isLoading, dateRange, forGroup, onForGroupToggle, productFilter, getData } = UseChartHook(props.url, { supportForGroup: true, startDate: props.startDate, endDate: props.endDate });
  const chartData: any = data;
  const [isBar, setIsBar] = useState(false);
  const toggleBar = () => setIsBar(!isBar);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={props.title} />
            <WebkuaToolBar
              quickFilter=''
              setQuickFilter={() => { }}
              disableSearch={true}
              productsFilter={productFilter}
              dateRange={dateRange}
              onSetDateRange={onDateChange}
              onRefresh={getData}
              tableFilters={[{
                label: t("standard.showForGroup"),
                checked: forGroup,
                onChange: onForGroupToggle
              }, {
                label: t("webkuaChart.showGraphAsLine"),
                checked: !isBar,
                onChange: toggleBar
              }]}
            />
            {isLoading ? <SalesChartSkeleton /> :
              chartData != null ?
                <ChartContent
                  data={chartData}
                  isBar={isBar}
                  dateRange={dateRange}
                  onDateChange={onDateChange}
                  forGroup={forGroup}
                  onForGroupToggle={onForGroupToggle}
                /> : <Alert severity="info" className='my-3'>
                  {t("webkuaChart.selectProductsTimePeriodEtcAndRefreshTableToRetrieveData")}
                </Alert>}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}
function ChartContent(props: {
  data: ChartData;
  dateRange: DateRange<Date>;
  onDateChange: (dateRange: DateRange<Date> | null) => void;
  forGroup: boolean;
  onForGroupToggle: (checked: boolean) => void;
  isBar: boolean
}) {
  const { t } = useTranslation();
  const chartData: any = props.data;
  const grouped = groupBy(chartData.datasets, "groupName");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const handleSelectGroup = (checked: boolean, label: string) => {
    checked ?
      setSelectedGroups([...selectedGroups, label]) :
      setSelectedGroups(selectedGroups.filter(g => g !== label));
  }

  const filteredData = chartData.datasets.filter((d: any) => {
    return selectedGroups.includes(d.groupName)
  });

  const updatedDataset = {
    labels: chartData.labels,
    datasets: filteredData
  }

  const keys = Object.keys(grouped);

  return <>
    <Row>
      {keys.map((k: string) =>
        <Col xs="auto" className='mb-3'>
          <CheckboxLabel
            value={selectedGroups.includes(k)}
            label={`${t("standard.show")} ${k}`}
            onChange={(event: any, value: boolean) => handleSelectGroup(value, k)}
          />
        </Col>)
      }
    </Row>
    {updatedDataset.datasets.length === 0 &&
      <Alert severity="info" className='my-3'>
        {props.data.datasets.length > 0 ? t("webkuaChart.selectDatasetToDisplayGraph") : t("webkuaChart.thereIsNoDataToDisplayForSelectedPeriod")}
      </Alert>
    }
    {props.isBar ?
      <Bar data={updatedDataset} /> :
      <Line data={updatedDataset} />
    }
  </>;
}

export default WebkuaChart;
